import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Table, Button, Modal, notification, Form, Input, Upload, Tag, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PictureOutlined, FileTextOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

export default function DailyQuoteList() {
  const [data,    setData]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [mode,    setMode]    = useState('text'); // 'text' | 'image'
  const [imgUrl,  setImgUrl]  = useState('');
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/daily-quotes?page=${page}&limit=30`, { headers: auth() });
      if (r.data.status) { setData(r.data.result || []); setTotal(r.data.total || 0); }
    } catch { notification.error({ message: 'Load failed', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { fetch(); }, [fetch]);

  const openNew = () => {
    setEditing(null); setMode('text'); setImgUrl('');
    form.resetFields(); setModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const m = row.image_url ? 'image' : 'text';
    setMode(m); setImgUrl(row.image_url || '');
    form.setFieldsValue({ quote_date: row.quote_date?.slice(0, 10) || '', quote_text: row.quote_text, author: row.author });
    setModal(true);
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('image', file);
      const r = await axios.post(`${baseUrl}/admin/daily-quotes/upload-image`, fd, { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } });
      if (r.data.status) { setImgUrl(r.data.url); notification.success({ message: 'Image uploaded ✅', placement: 'topRight' }); }
    } catch { notification.error({ message: 'Upload failed', placement: 'topRight' }); }
    finally { setUploading(false); }
    return false; // prevent default antd upload
  };

  const save = async () => {
    try {
      const vals = await form.validateFields();
      const payload = {
        quote_date: vals.quote_date,
        author:     vals.author || null,
        quote_text: mode === 'text' ? (vals.quote_text || null) : null,
        image_url:  mode === 'image' ? (imgUrl || null) : null,
      };
      if (!payload.quote_text && !payload.image_url)
        return notification.warning({ message: 'Please add text or upload image', placement: 'topRight' });

      if (editing) {
        await axios.patch(`${baseUrl}/admin/daily-quotes/${editing.id}`, payload, { headers: auth() });
      } else {
        await axios.post(`${baseUrl}/admin/daily-quotes`, payload, { headers: auth() });
      }
      notification.success({ message: editing ? 'Updated ✅' : 'Saved ✅', placement: 'topRight' });
      setModal(false); fetch();
    } catch (err) {
      if (err?.response?.data?.message) notification.error({ message: err.response.data.message, placement: 'topRight' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this quote?')) return;
    await axios.delete(`${baseUrl}/admin/daily-quotes/${id}`, { headers: auth() });
    notification.success({ message: 'Deleted', placement: 'topRight' });
    fetch();
  };

  const columns = [
    { title: 'Date', dataIndex: 'quote_date', render: v => v?.slice(0, 10), width: 110 },
    { title: 'Type', render: (_, r) => r.image_url ? <Tag color="blue" icon={<PictureOutlined />}>Image</Tag> : <Tag color="green" icon={<FileTextOutlined />}>Text</Tag>, width: 80 },
    { title: 'Content', render: (_, r) => r.image_url
        ? <img src={r.image_url} alt="quote" style={{ height: 40, borderRadius: 4 }} />
        : <span style={{ fontSize: 12, color: '#374151' }}>{r.quote_text?.slice(0, 80)}…</span>
    },
    { title: 'Author', dataIndex: 'author', render: v => v || '—', width: 140 },
    { title: 'Active', dataIndex: 'is_active', render: v => <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag>, width: 70 },
    { title: 'Actions', render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remove(r.id)} />
      </Space>
    ), width: 90 },
  ];

  return (
    <div style={{ padding: '12px 0' }}>
      <Card
        title={<><FileTextOutlined style={{ color: '#7a0000', marginRight: 8 }} />Daily Quotes / आज का विचार</>}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openNew} style={{ background: '#7a0000' }}>Add Quote</Button>}
      >
        <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small"
          pagination={{ current: page, pageSize: 30, total, onChange: setPage, showTotal: t => `Total ${t}` }} />
      </Card>

      <Modal open={modal} title={editing ? 'Edit Quote' : 'Add Quote'} onOk={save} onCancel={() => setModal(false)} width={520}>
        <Form form={form} layout="vertical">
          <Form.Item name="quote_date" label="Quote Date" rules={[{ required: true }]}>
            <Input type="date" style={{ width: '100%' }} />
          </Form.Item>

          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <Button type={mode === 'text' ? 'primary' : 'default'} icon={<FileTextOutlined />} onClick={() => setMode('text')}>Text Quote</Button>
            <Button type={mode === 'image' ? 'primary' : 'default'} icon={<PictureOutlined />} onClick={() => setMode('image')}>Image Quote</Button>
          </div>

          {mode === 'text' ? (
            <>
              <Form.Item name="quote_text" label="Quote (Hindi / Sanskrit)" rules={[{ required: true }]}>
                <Input.TextArea rows={4} placeholder="सत्यं वद, धर्मं चर..." />
              </Form.Item>
              <Form.Item name="author" label="Author / Source">
                <Input placeholder="श्री खेतेश्वर महाराज / वेद / गीता..." />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item label="Upload Quote Image">
                <Upload beforeUpload={uploadImage} accept="image/*" showUploadList={false}>
                  <Button icon={<UploadOutlined />} loading={uploading}>Upload Image</Button>
                </Upload>
              </Form.Item>
              {imgUrl && <img src={imgUrl} alt="preview" style={{ width: '100%', borderRadius: 8, marginTop: 8, maxHeight: 200, objectFit: 'cover' }} />}
              <Form.Item name="author" label="Author (optional)">
                <Input placeholder="Author name..." />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
}
